/* eslint-disable @next/next/no-head-element */
import { CircularProgress, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import '@/styles/globals.css'
import NextApp, { type AppContext, type AppProps } from 'next/app'
import Layout from './Layout'
import Cookies from 'js-cookie'
import Login from './login'
import { useRouter } from 'next/router'
import CreateAccount from './create-account'
import ForgotPassword from './forgot-password'
import Announcements from './announcements'
import { customSiteRedirectUrl, useStore } from '@/src/types/store'
import AnnouncementDetail from './announcement-detail/[slug]'
import Updates from './news-insights'
import { ThemeProvider } from '@mui/material/styles'
import { theme } from '@/src/theme/index'
import useUpdateTheme from '@/src/hooks/useUpdateTheme'
import { handleSessionVisits } from '@/src/types/helper'
import Terms from './terms'
import Head from 'next/head'
import CommonApi from './api'
import Test from './test-meta'
import Research from './research'
import Corporate from './corporate'
import { useCognitoStore } from '@/src/types/CognitoStore'
import Dashboard from '.'
import { useAnnouncementStore } from '@/src/types/announcement'
import CustomModal from '@/src/components/Modal'
import PortalPasswordForm from '@/src/components/portal-password'
import { PortalPassEndpoints } from '@/pages/api/endpoints/PortalPassword'
import { useHeaderContentStore } from '@/src/types/headerContent'
import ViewDns from './view-dns'
import MaintainanceMode from './maintainance-mode'
import GlobalToast from '@/src/components/GlobalToast'
import DomainInactive from '@/src/components/domain-inactive'
import { useDomainStore } from '@/src/types/DomainStore'
import LandingPage from '@/src/components/landing-page'
import LandingPageFallback from '@/src/components/landing-page/Fallback'
import ErrorBoundary from '@/src/components/error-boundary'
import { getTenantSeo, type TenantSeo } from '@/src/utils/tenantSeo'
import { useTier, type PublicSurface } from '@/src/hooks/useTier'
import { adminRouteAccess } from '@/src/utils/adminRouteAccess'

// Shown when the tenant has no logo_mark, instead of the browser's blank globe.
const DEFAULT_FAVICON =
  'https://docs.relait.com.au/UAT Brooksides/theme/1732538301365715/RelaitIconLogoLight.svg'

/**
 * The public investor-centre routes. A landing tenancy serves none of them:
 * '/' becomes the landing page and the rest redirect to it.
 *
 * Kept at module scope so it is a stable reference, and exported so a test
 * can assert it against the pages directory — adding a public IC route here
 * is the step that is easy to forget.
 */
export const PUBLIC_IC_ROUTES = [
  '/',
  '/announcements',
  '/announcement-dashboard',
  '/news-insights',
  '/research',
  '/corporate',
  '/announcement-detail/[slug]'
]

// Origin-scoped, so one tenancy can never read another's value.
const SURFACE_CACHE_KEY = 'relait:public_surface'

const readCachedSurface = (): PublicSurface | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    const value = window.localStorage.getItem(SURFACE_CACHE_KEY)
    return value === 'landing_page' || value === 'investor_centre' ? value : undefined
  } catch {
    // Private mode or storage disabled: fall back to waiting for /theme.
    return undefined
  }
}

const writeCachedSurface = (surface: PublicSurface) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SURFACE_CACHE_KEY, surface)
  } catch {
    // Nothing to do — the cache is an optimisation, never a source of truth.
  }
}

interface Data {
  email: string
  password: string
  id?: number
}

export default function App({
  Component,
  pageProps,
  tenantSeo
}: AppProps & { tenantSeo: TenantSeo | null }) {
  const newTheme = useUpdateTheme(theme)
  const userId = Cookies.get('user_id')
  const { setCaptchaApiKey } = useCognitoStore()
  const { setHeaderCategories } = useAnnouncementStore()
  const accessToken = Cookies.get('access_token')
  const router = useRouter()
  const [modal, setModal] = useState(false)
  const [isLogged, setIsLogged] = useState(false)
  const [isWidgetLogin, setIsWidgetLogin] = useState(false)
  const [firstRender, setFirstRender] = useState(true)
  const [visitData, setVisitData] = useState<any>('')
  // Seeded from the server-fetched tenant branding so the very first HTML
  // response already carries the right title/meta for social crawlers, which
  // never run the client-side /theme fetch below.
  const [businessDetails, setBusinessDetails] = useState({
    businessName: tenantSeo?.businessName ?? '',
    businessCode: tenantSeo?.businessCode ?? ''
  })
  // When the theme has hide_from_google enabled, this site is kept out of search engine indexes
  const [hideFromGoogle, setHideFromGoogle] = useState(tenantSeo?.hideFromGoogle ?? false)
  const [loading, setLoading] = useState(true)
  // Set centrally by CommonApi as soon as any endpoint reports the domain as
  // inactive/deactivated; the whole app is then replaced by a message page.
  const { domainInactive, domainInactiveMsg } = useDomainStore()

  useEffect(() => {
    if (router.isReady) {
      setIsWidgetLogin(
        router.pathname === '/login' && router.query?.redirect_url === '__widget_login__'
      )
    }
  }, [router.isReady, router.pathname, router.query?.redirect_url])

  const {
    fetchProfile,
    fetchPermissions,
    setThemeColours,
    setTextColorType,
    themeColours,
    setThemeLogos,
    setHomeTheme,
    setThemeLoading,
    themeLogo,
    profile,
    themeLoading,
    setCompanyThumbnails,
    openToast,
    homeTheme,
    profileLoading
  } = useStore()

  const { fetchHeaderItems } = useHeaderContentStore()

  // Project Boston: Essentials and Intelligence tenancies have no public
  // investor centre. Their root serves the branded landing page instead; the
  // BE locks the IC endpoints down too. Surface-derived, never plan-derived —
  // see useTier for why unresolved resolves to "not landing".
  const tier = useTier()
  const isLandingTenant = tier.isLandingTenant

  // The public IC routes a landing tenancy does not serve. Only '/' renders
  // the landing page; the rest redirect to it, so one page keeps one URL.
  const isPublicIcRoute = PUBLIC_IC_ROUTES.includes(router?.pathname)
  const isRootRoute = router?.pathname === '/'

  // Remembering the resolved surface lets a repeat visitor render the
  // investor centre on the first paint instead of waiting behind /theme.
  // Deliberately trusted in one direction only: a cached 'investor_centre'
  // skips the spinner, but a cached 'landing_page' does NOT render the
  // landing page early. Showing the IC to a downgraded tenant for one frame
  // is a cosmetic flash; showing the landing page to a 360 tenant would
  // replace a listed company's public site on the strength of a stale cache.
  const [icEarlyRenderOk, setIcEarlyRenderOk] = useState(false)

  useEffect(() => {
    setIcEarlyRenderOk(readCachedSurface() === 'investor_centre')
  }, [])

  useEffect(() => {
    if (!tier.surface) return
    writeCachedSurface(tier.surface)
  }, [tier.surface])

  // Block IC pages only when we genuinely do not know the surface yet.
  const holdIcRender = isPublicIcRoute && themeLoading && !icEarlyRenderOk

  const showAuthScreens = () => {
    switch (router?.pathname) {
      case '/create-account':
        return <CreateAccount />
      case '/forgot-password':
        return <ForgotPassword />
      case '/login':
        return <Login />
      case '/announcements':
        return <Announcements />
      case '/news-insights':
        return <Updates />
      case '/research':
        return <Research />
      case '/corporate':
        return <Corporate />
      case '/':
        return <Dashboard />
      case '/announcement-dashboard':
        return <Dashboard />
      case '/terms':
        return <Terms />
      case '/view-dns':
        return <ViewDns />
      case '/maintainance-mode':
        return <MaintainanceMode />
      case '/announcement-detail/[slug]':
        return <AnnouncementDetail {...pageProps} />
      case '/test-meta':
        return <Test {...pageProps} />
    }
  }

  // Utility: Convert any CSS-valid color to [r, g, b]
  const getRGBFromColor = (color: string): [number, number, number] => {
    const el = document.createElement('div')
    el.style.color = color
    document.body.appendChild(el)

    const computedColor = getComputedStyle(el).color
    document.body.removeChild(el)

    const result = computedColor.match(/\d+/g)
    if (!result || result.length < 3) {
      throw new Error(`Invalid color: ${color}`)
    }

    return [parseInt(result[0]), parseInt(result[1]), parseInt(result[2])]
  }

  // Utility: Check if the color is dark based on luminance
  const isColorDark = (color: string): boolean => {
    const [r, g, b] = getRGBFromColor(color)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance < 0.5
  }

  useEffect(() => {
    const setTextColor = () => {
      const primaryColor = themeColours.primary
      const secondaryColor = themeColours.secondary
      try {
        const primary_textColor = isColorDark(primaryColor) ? true : false
        const secondary_textColor = isColorDark(secondaryColor) ? true : false
        setTextColorType({
          primaryTextDark: primary_textColor,
          secondaryTextDark: secondary_textColor
        })
      } catch (err) {
        console.error('Invalid color format:', primaryColor)
        setTextColorType({ primaryTextDark: true, secondaryTextDark: false })
      }
    }

    setTextColor()
  }, [themeColours.primary, themeColours.secondary])

  // Resolves the tenant theme and returns the raw payload so callers can key
  // follow-up fetches off the plan; returns null when the request fails.
  const handleGetTheme = async () => {
    setThemeLoading(true)
    try {
      const resp = await CommonApi({
        method: 'get',
        path: `theme${userId ? `?id=${userId}` : ''}`
      })
      const themeData = resp.data
      setThemeColours({
        primary: themeData.p_color,
        secondary: themeData.s_color,
        email_bg_color: themeData.email_bg_color
      })
      setCompanyThumbnails(themeData?.thumbnails)
      setThemeLogos({
        default_logo: themeData.default_logo,
        logo: themeData.logo,
        dark_logo: themeData.dark_logo,
        logo_mark: themeData.logo_mark,
        dark_logo_mark: themeData.dark_logo_mark,
        logo_file_name: themeData?.files?.logo,
        dark_logo_file_name: themeData?.files?.dark_logo,
        logo_mark_file_name: themeData?.files?.logo_mark,
        dark_logo_mark_file_name: themeData?.files?.dark_logo_mark,
        logo_mark_size: themeData.logo_mark_size,
        dark_logo_mark_size: themeData.dark_logo_mark_size,
        logo_size: themeData.logo_size,
        dark_logo_size: themeData.dark_logo_size
      })
      setHomeTheme({
        code: themeData?.code,
        banner: themeData.banner,
        banner_file_name: themeData?.files?.banner,
        login_banner: themeData?.login_banner ?? null,
        title: themeData.title,
        text: themeData.text,
        announcement_stream: themeData.announcement_stream,
        notification_stream: themeData.notification_stream,
        primary_domain: themeData?.primary_domain,
        show_new_dashboard: themeData?.show_new_dashboard,
        active_dashboards: themeData?.active_dashboards,
        share_registry: themeData?.share_registry,
        has_transaction_feed: themeData?.has_transaction_feed,
        enable_placements: themeData?.enable_placements,
        enable_post_ipo: themeData?.enable_post_ipo ?? false,
        plan: themeData?.plan,
        public_surface: themeData?.public_surface,
        widget_signup: themeData?.widget_signup,
        investor_login: themeData?.investor_login,
        corporate_home_url: themeData?.corporate_home_url ?? null,
        business_name: themeData?.business_name,
        enable_email_logo: themeData?.enable_email_logo,
        central_banner: themeData?.central_banner ?? null,
        auto_pin_presentation: themeData?.auto_pin_presentation,
        send_auto_email: themeData?.send_auto_email,
        segment_ids: themeData?.segment_ids,
        ps_header: themeData?.ps_header,
        ps_salutation: themeData?.ps_salutation,
        ps_footer: themeData?.ps_footer,
        custom_site_url: themeData?.custom_site_url,
        custom_site_announcements_url: themeData?.custom_site_announcements_url,
        investor_centre_url: themeData?.investor_centre_url,
        is_custom_site: themeData?.is_custom_site,
        show_short_summaries: themeData?.show_short_summaries ?? false,
        hide_daily_report: themeData?.hide_daily_report ?? false,
        news_search_terms: themeData?.news_search_terms ?? [],
        // Older cached /theme responses omit this — default to the
        // long-standing new-tab behaviour.
        home_link_behaviour: themeData?.home_link_behaviour ?? 'new_tab',
        youtube_sync_enabled: themeData?.youtube_sync_enabled ?? false,
        youtube_channel_url: themeData?.youtube_channel_url ?? null,
        youtube_source_title: themeData?.youtube_source_title ?? null,
        youtube_auto_publish: themeData?.youtube_auto_publish ?? false,
        youtube_backfill_count: themeData?.youtube_backfill_count ?? 0,
        youtube_last_synced_at: themeData?.youtube_last_synced_at ?? null,
        auto_show_latest_videos: themeData?.auto_show_latest_videos ?? false
      })
      setBusinessDetails({
        businessName: themeData.business_name,
        businessCode: themeData.code
      })
      setHideFromGoogle(themeData?.hide_from_google ?? false)
      if (themeData.toggle && sessionStorage.getItem('portalVerification') !== 'true') {
        setModal(true)
      }
      setThemeLoading(false)
      return themeData
    } catch (err: any) {
      console.log('Theme ERR', err?.response)
      if (err?.response?.status === 500) {
        router.push('/maintainance-mode')
      }
    }
    setThemeLoading(false)
    return null
  }

  const handleFetchCognitoCreds = async () => {
    try {
      const resp = await CommonApi({
        path: 'cognito-ref',
        method: 'get'
      })
      if (resp?.status) {
        setCaptchaApiKey(resp?.data?.cognito_captcha_key)
      }
    } catch (error) {}
  }
  const fetchCategories = async () => {
    try {
      const resp = await CommonApi({
        path: 'announcement/headers/category',
        method: 'get'
      })

      if (resp?.status) {
        setHeaderCategories(resp?.data)
      }
    } catch (err) {
      console.log('err', err)
    }
  }

  useEffect(() => {
    // Fired in parallel, not chained. Header content and categories do not
    // depend on the theme response, and awaiting it put a full round-trip in
    // front of first load for every 360 tenant — the majority — purely to
    // spare the minority two harmless 404s.
    //
    // Those 404s are avoided from the second visit onward via the cached
    // surface; the very first visit for a landing tenancy makes two requests
    // the BE rejects, which is cheaper than the round-trip it replaces.
    handleGetTheme()
    if (readCachedSurface() !== 'landing_page') {
      fetchHeaderItems(router?.pathname) // header content
      fetchCategories()
    }
    handleFetchCognitoCreds()
    if (accessToken) {
      fetchProfile()
      fetchPermissions()
      setIsLogged(true)
    } else {
      setIsLogged(false)
      if (
        typeof window !== 'undefined' &&
        window.location.hostname.startsWith('portal') &&
        router.pathname === '/'
      ) {
        router.replace('/login')
      }
    }
    setLoading(false)
  }, [accessToken]) //main

  useEffect(() => {
    const trackVisit = async () => {
      try {
        let response
        if (Cookies.get('access_token') && profile.id && profile.user_type === 'investor') {
          response = await handleSessionVisits(true, true, visitData, true, true)
        } else {
          response = await handleSessionVisits(true, false, visitData, true)
        }
        if (response?.portal_access && sessionStorage.getItem('portalVerification') !== 'true') {
          setModal(true)
        }
      } catch (error) {
        console.error('Error in trackVisit:', error)
      }
      setFirstRender(false)
    }

    trackVisit()
  }, [visitData])

  useEffect(() => {
    const trackSessionData = async () => {
      try {
        const VisitData = sessionStorage.getItem('visitData')

        if (!Cookies.get('access_token')) {
          if (firstRender) {
            setVisitData(VisitData)
            if (!visitData) {
              setTimeout(() => {
                const newVisitData = sessionStorage.getItem('visitData')
                setVisitData(newVisitData)
              }, 2000)
            }
          }
        }

        let response
        if (!firstRender && !Cookies.get('access_token')) {
          response = await handleSessionVisits(true, false)
        } else if (
          !firstRender &&
          Cookies.get('access_token') &&
          profile.user_type === 'investor'
        ) {
          response = await handleSessionVisits(true, false)
        }
        if (response?.portal_access && sessionStorage.getItem('portalVerification') !== 'true') {
          setModal(true)
        }

        if (profile?.user_type === 'investor') {
          if (
            router.pathname === '/admin' ||
            router.pathname.includes('content') ||
            router.pathname.includes('admin') ||
            router.pathname.includes('role') ||
            router.pathname.includes('theme')
          ) {
            router.push('my-profile')
          }
        }
      } catch (error) {
        console.error('Error in trackSessionData:', error)
      }
    }

    trackSessionData()
  }, [router.pathname, profile])

  // Custom site redirect for public-facing pages
  const customSiteRedirectRoutes = [
    '/',
    '/announcements',
    '/news-insights',
    '/research',
    '/corporate'
    // '/announcement-detail/[slug]'
  ]

  useEffect(() => {
    if (themeLoading) return
    // Landing tenants serve the branded landing page at these routes; the
    // company website is reachable from its "Visit our website" link instead.
    if (isLandingTenant) return
    const siteUrl = customSiteRedirectUrl(homeTheme, router.pathname)
    if (!siteUrl) return
    if (!customSiteRedirectRoutes.includes(router.pathname)) return

    if (!accessToken) {
      window.location.href = siteUrl
      return
    }

    if (profileLoading) return

    if (profile.user_type !== 'investor') {
      // /corporate?preview=true is the staff Corporate Preview, not the public page.
      if (router.query.preview === 'true') return
      router.push('/admin')
    } else {
      window.location.href = siteUrl
    }
  }, [
    themeLoading,
    homeTheme,
    accessToken,
    profileLoading,
    profile,
    router.pathname,
    router.query.preview
  ])

  // Landing tenants: logged-in staff who hit a public IC route go to the
  // admin app (the IC pages are locked down for these plans on the BE too).
  useEffect(() => {
    if (themeLoading || !isLandingTenant || !accessToken) return
    if (!isPublicIcRoute) return
    router.push('/admin')
  }, [themeLoading, isLandingTenant, accessToken, isPublicIcRoute, router])

  // Landing tenants serve exactly one public page, at the root. Without this
  // the same landing content renders under all seven IC paths, each with its
  // own URL — duplicate content on a listed company's domain, and an
  // unbounded set of them via /announcement-detail/[slug].
  //
  // replace(), not push(): the IC path was never a real page for this
  // tenancy, so it should not occupy a history entry the back button returns
  // to. Signed-in staff are excluded — the effect above sends them to /admin.
  useEffect(() => {
    if (themeLoading || !isLandingTenant || accessToken) return
    if (!isPublicIcRoute || isRootRoute) return
    router.replace('/')
  }, [themeLoading, isLandingTenant, accessToken, isPublicIcRoute, isRootRoute, router])

  // Derived during render so a blocked page is never mounted: its effects and
  // API calls would otherwise run before the redirect lands.
  const routeAccess = adminRouteAccess({
    pathname: router.pathname,
    isLoggedIn: !!accessToken,
    themeLoading,
    tierResolved: tier.isResolved,
    isEssentials: tier.isEssentials,
    profileLoading,
    profileId: profile.id,
    userType: profile.user_type
  })

  useEffect(() => {
    if (routeAccess === 'blocked') router.replace('/manage-content')
  }, [routeAccess, router])


  function capitalizeWords(str: string) {
    const newStr = str.replace(/\b\w/g, function (char) {
      return char.toUpperCase()
    })
    return newStr === 'Updates' ? 'News & Insights' : newStr
  }

  const pageName =
    router.pathname === '/admin'
      ? 'Dashboard'
      : capitalizeWords(router.asPath.replace('/', '').replace('-', ' '))

  const brand = [
    businessDetails.businessName,
    businessDetails.businessName && businessDetails.businessCode
      ? `ASX: ${businessDetails.businessCode}`
      : ''
  ]
    .filter(Boolean)
    .join(' | ')

  const title = [brand, router.pathname !== '/' ? pageName : ''].filter(Boolean).join(' - ')

  const seoDescription = businessDetails.businessName
    ? `Investor Centre for ${businessDetails.businessName}${
        businessDetails.businessCode ? ` (ASX: ${businessDetails.businessCode})` : ''
      }. Access the latest announcements, news and company updates.`
    : ''

  // First thumbnail from Settings > Company & theme, falling back to the logo.
  const seoImage = tenantSeo?.ogImage || themeLogo?.logo || ''
  // The favicon is rendered through <Head> only — no direct DOM mutation — so a
  // single <link rel="icon"> is owned by React and navigation can't make two of
  // them fight. It stays empty until the theme resolves, otherwise the default
  // icon is injected first and Chrome caches it for this URL, keeping the wrong
  // icon even once the client's own logo_mark arrives.
  const seoFavicon =
    tenantSeo?.favicon ||
    (themeLogo as { logo_mark?: string })?.logo_mark ||
    (themeLoading ? '' : DEFAULT_FAVICON)
  // A landing tenancy has one public page. Advertising the current path
  // would publish the same content under every IC route it is reachable at,
  // so both the canonical and the social URLs collapse to the root.
  const seoUrl = tenantSeo?.siteOrigin
    ? `${tenantSeo.siteOrigin}${isLandingTenant ? '/' : router.asPath}`
    : ''
  const canonicalUrl = isLandingTenant ? seoUrl : ''
  // Belt and braces for a crawler that fetches a non-root IC path before the
  // redirect runs: tell it not to index this copy, but do follow through to
  // the canonical root.
  const robotsContent = hideFromGoogle
    ? 'noindex, nofollow'
    : isLandingTenant && !isRootRoute
      ? 'noindex, follow'
      : ''

  const handlePortalPassword = async (values: Data) => {
    setLoading(true)
    try {
      const response = await CommonApi({
        path: PortalPassEndpoints?.post,
        method: 'post',
        data: {
          password: values?.password
        }
      })
      if (response?.status) {
        setModal(false)
        sessionStorage.setItem('portalVerification', 'true')
      } else {
      }
    } catch (error: any) {
      openToast(error.response?.data?.msg || 'something went wrong!', 'error')
    }
    setLoading(false)
  }

  useEffect(() => {
    const currentVersion = '1.0.1'
    const storedVersion = localStorage.getItem('appVersion')

    if (!storedVersion || storedVersion !== currentVersion) {
      localStorage.setItem('appVersion', currentVersion)

      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name)
          })
        })
      }

      window.location.reload()
    }
  }, [])

  return (
    <ThemeProvider theme={newTheme}>
      <Head>
        <title>{title}</title>
        {seoFavicon && <link key="favicon" rel="icon" href={seoFavicon} />}
        {seoDescription && <meta name="description" content={seoDescription} />}
        {/* Defaults for every page. next/head only dedupes meta tags by `key`
            (never by `property`), so every tag below carries one — that lets a
            page with its own <Head> (e.g. an announcement detail) replace these
            instead of both ending up in the HTML. */}
        <meta key="og:type" property="og:type" content="website" />
        {businessDetails.businessName && (
          <meta key="og:site_name" property="og:site_name" content={businessDetails.businessName} />
        )}
        <meta key="og:title" property="og:title" content={title} />
        {seoDescription && (
          <meta key="og:description" property="og:description" content={seoDescription} />
        )}
        {seoImage && <meta key="og:image" property="og:image" content={seoImage} />}
        {seoUrl && <meta key="og:url" property="og:url" content={seoUrl} />}
        <meta key="twitter:card" property="twitter:card" content="summary_large_image" />
        <meta key="twitter:title" property="twitter:title" content={title} />
        {seoDescription && (
          <meta key="twitter:description" property="twitter:description" content={seoDescription} />
        )}
        {seoImage && <meta key="twitter:image" property="twitter:image" content={seoImage} />}
        {seoUrl && <meta key="twitter:url" property="twitter:url" content={seoUrl} />}
        {canonicalUrl && <link key="canonical" rel="canonical" href={canonicalUrl} />}
        {robotsContent && <meta key="robots" name="robots" content={robotsContent} />}
      </Head>
      {domainInactive ? (
        <DomainInactive message={domainInactiveMsg} />
      ) : modal ? (
        ''
      ) : holdIcRender ? (
        // Only reached when the surface is genuinely unknown — a first visit,
        // or storage unavailable. A returning investor-centre visitor renders
        // straight through on the cached surface instead of waiting here.
        <div className="min-h-[100dvh] flex items-center justify-center">
          <CircularProgress size={30} />
        </div>
      ) : isLandingTenant && isPublicIcRoute ? (
        // Landing page wins over both the IC pages and the logged-in layout;
        // logged-in staff are redirected to /admin by the effect above.
        //
        // Boundaried because for these tenancies this component IS the
        // company's public web presence: an unhandled render error here is a
        // white screen on a listed company's root domain. The fallback keeps
        // the one thing a visitor might actually need — a way to reach the
        // real site — and staff can still reach the portal via /login.
        <ErrorBoundary fallback={<LandingPageFallback />}>
          <LandingPage />
        </ErrorBoundary>
      ) : isLogged && !isWidgetLogin ? (
        <>
          <Layout>
            {routeAccess === 'allowed' ? <Component {...pageProps} /> : null}
          </Layout>
        </>
      ) : (
        showAuthScreens()
      )}
      <CustomModal classes="p-4 relative" open={modal && !domainInactive} crossIcon={false}>
        <Typography variant="h6">Access Portal</Typography>
        <PortalPasswordForm onSubmit={handlePortalPassword} loading={loading} />
      </CustomModal>
      <GlobalToast />
    </ThemeProvider>
  )
}

// Runs on the server for every request so the tenant's branding is baked into
// the HTML before it reaches a social crawler. On client-side navigations there
// is no `req`, getTenantSeo returns null and the client store stays the source
// of truth.
App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)
  const tenantSeo = await getTenantSeo(appContext.ctx.req)

  return { ...appProps, tenantSeo }
}
